// Which level, and which tier, a lesson entry opens on (MUS-495, MUS-496).
//
// This lived inline in the session-init effect in `MainAppPhaser`, where three
// things went wrong at once and none of them were reachable by a test:
//
//  - the progress row was looked up by comparing a level NUMBER against an array
//    INDEX (`ulp.level.level_number === data.levelSelect`), so the tier was
//    derived from a neighbouring level's progress;
//  - the derivation had no name, so the same computation was re-typed at three
//    other call sites and two of those got the index/number distinction wrong
//    in the same way, independently;
//  - there was nowhere to assert "a beaten level's Tier 1 opens on Tier 1",
//    which is MUS-495's acceptance criterion.
//
// The index-to-number resolution is deliberately INSIDE this function rather
// than left to callers. Callers holding a bare `levelSelect` are exactly how the
// bug happened, so the one place that knows `levelSelect` indexes `levelData`
// is here, and it hands back the resolved number for the caller to use
// downstream.

import { LevelData, UserLevelData } from 'Types';

export type EntryTierInputs = {
  /** All levels, in roadmap order. `levelSelect` indexes into this. */
  levelData: LevelData[] | undefined;
  /** The selected level's INDEX into `levelData`. Not a level number. */
  levelSelect: number;
  /** Every level's progress row for this user, keyed by level number. */
  userLevelData: UserLevelData[] | undefined;
  /** The tier this level was last selected or played at in this app session, or
   *  null on a fresh app open. See `Utils/TierMemory`. */
  rememberedTier: number | null;
};

export type EntryTier = {
  /** The selected level's real `level_number`, or null when `levelSelect` does
   *  not address a loaded level (nothing should be created in that case). */
  levelNumber: number | null;
  /** The tier to open on. Meaningless when `levelNumber` is null. */
  tierNumber: number;
};

/** The progress-derived default, used when this app session has no memory of
 *  the level:
 *   - finished level -> replay the last tier that was completed;
 *   - level in progress -> continue to the next unplayed tier;
 *   - never played -> tier 1.  */
function progressDerivedTier(progress: UserLevelData | undefined): number {
  if (!progress) return 1;
  const highestComplete = progress.highest_tier_complete ?? 0;
  if (highestComplete < 1) return 1;
  return progress.level_completed_at ? highestComplete : highestComplete + 1;
}

function isUsableTier(tier: number | null): tier is number {
  return tier !== null && Number.isInteger(tier) && tier >= 1;
}

/**
 * Resolve the selected level and the tier it should open on.
 *
 * A tier remembered from this app session wins outright. That is the MUS-496
 * decision - "each level remembers the tier the user last selected or played
 * there" - and it holds whether or not the level is finished: a user who has
 * cleared tiers 1 and 2 of a three-tier level and has gone back to replay tier 1
 * should land on tier 1, not be thrown forward to the tier they have not
 * reached yet.
 *
 * The tier is a request, not a guarantee: `createLevelSession` clamps it to the
 * level's real tier count when the level's tier list loads. When that list fails
 * to load, it is capped at the tier count loaded at startup if that is known,
 * and otherwise opened as asked.
 */
export function resolveEntryTier({
  levelData,
  levelSelect,
  userLevelData,
  rememberedTier,
}: EntryTierInputs): EntryTier {
  const levelNumber = levelData?.[levelSelect]?.level_number ?? null;
  if (levelNumber === null) {
    return { levelNumber: null, tierNumber: 1 };
  }
  if (isUsableTier(rememberedTier)) {
    return { levelNumber, tierNumber: rememberedTier };
  }
  const progress = userLevelData?.find((ulp) => ulp.level?.level_number === levelNumber);
  return { levelNumber, tierNumber: progressDerivedTier(progress) };
}

/**
 * Where to leave the tier memory once a tier has been played to completion
 * (MUS-496).
 *
 * Finishing a tier and then leaving to the roadmap without paging through the
 * wrap-up is ambiguous: the tier just played is genuinely "the tier you were
 * last on", but if finishing it UNLOCKED the next one, coming back to the tier
 * you have already cleared is not where you want to be.
 *
 * The signal that separates the two cases is whether the completion moved the
 * frontier:
 *
 *  - It did (`completedTier` beat `highestTierCompleteBefore`): this was
 *    progression, a new tier just unlocked, and that unlocked tier is where the
 *    user belongs on re-entry.
 *  - It did not: this was a replay of a tier already cleared. The user went back
 *    to it deliberately, so leave them on it - bumping them forward would undo
 *    the review they chose.
 *
 * Completing the last tier clamps to itself, which is also the progress-derived
 * default for a finished level, so the two agree.
 */
export function tierToRememberAfterCompleting({
  completedTier,
  highestTierCompleteBefore,
  levelTierCount,
}: {
  completedTier: number;
  /** `highest_tier_complete` as it stood BEFORE this completion was recorded. */
  highestTierCompleteBefore: number | null | undefined;
  /** How many tiers the level has. Used only to clamp. */
  levelTierCount: number | null | undefined;
}): number {
  const advancedTheFrontier = completedTier > (highestTierCompleteBefore ?? 0);
  if (!advancedTheFrontier) {
    return completedTier;
  }
  const nextTier = completedTier + 1;
  const hasUsableCount = typeof levelTierCount === 'number' && levelTierCount >= 1;
  // Without a trustworthy tier count, hand back the unclamped next tier.
  // createLevelSession clamps it when the level's tier list loads, and on a
  // tier list failure caps it at the startup tier count when that is known.
  return hasUsableCount ? Math.min(nextTier, levelTierCount) : nextTier;
}
